import { useRef, useCallback, useEffect } from "react";
import AudioController from "./audioPlayer/AudioController";
import { resolveMediaUrl } from "../assets/utils/mediaUrl";
import q from "../styles/components/examQuestion.module.scss";
import { ReactComponent as PauseCircleFilled } from "../assets/icons/pause-circle-filled.svg";
import { ReactComponent as PlayCircleFilled } from "../assets/icons/play-circle-filled.svg";

const formatTime = (seconds) => {
    const s = Number(seconds);
    if (!Number.isFinite(s) || s < 0) return "0:00";
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return `${m}:${String(r).padStart(2, "0")}`;
};

const QuestionAudioPlayer = ({ audioURL, id, isActive = true, onActivate }) => {
    const trackRef = useRef(null);
    const resolvedAudioURL = audioURL ? resolveMediaUrl(audioURL) : '';
    const { playing, toggle, setPlaying, duration, curTime, setClickedTime } = AudioController(resolvedAudioURL);
    const progress = duration > 0 ? Math.min(100, (curTime / duration) * 100) : 0;

    useEffect(() => {
        if (!isActive && playing) {
            setPlaying(false);
        }
    }, [isActive, playing, setPlaying]);

    const handleToggle = () => {
        if (!playing) {
            onActivate?.();
        }
        toggle();
    };

    const seekFromEvent = useCallback((e) => {
        const track = trackRef.current;
        if (!track || !duration) return;
        const rect = track.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        setClickedTime(ratio * duration);
    }, [duration, setClickedTime]);

    const onTrackMouseDown = (e) => {
        seekFromEvent(e);
        const onMove = (ev) => seekFromEvent(ev);
        const onUp = () => {
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
        };
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
    };

    if (!audioURL) return null;

    return (
        <div className={q.audioPlayer} key={id}>
            <button
                type="button"
                className={q.audioPlayBtn}
                onClick={handleToggle}
                aria-label={playing ? "توقف" : "پخش"}
            >
                {playing ? (
                    <PauseCircleFilled className={q.audioPlayIcon} />
                ) : (
                    <PlayCircleFilled className={q.audioPlayIcon} />
                )}
            </button>
            <div className={q.audioMain}>
                <div
                    ref={trackRef}
                    className={q.audioTrack}
                    onMouseDown={onTrackMouseDown}
                    role="slider"
                    aria-valuemin={0}
                    aria-valuemax={duration || 0}
                    aria-valuenow={curTime || 0}
                >
                    <div className={q.audioFill} style={{ width: `${progress}%` }} />
                    <span className={q.audioKnob} style={{ left: `${progress}%` }} />
                </div>
                <div className={q.audioTimes}>
                    <span>{formatTime(curTime)}</span>
                    <span>{formatTime(duration)}</span>
                </div>
            </div>
        </div>
    );
};

export default QuestionAudioPlayer;
